<script setup lang="ts">
import { computed } from 'vue'
import FundingEnvelopeListItemConsumption from './FundingEnvelopeListItemConsumption.vue'
import { FundingAllocationType } from '../../../../Constants/FundingAllocationType.ts'
import type { FundingEnvelope } from './types'

const props = withDefaults(
  defineProps<{
    accordionId: string
    envelope: FundingEnvelope
    expanded: boolean
  }>(),
  { expanded: true },
)

const emit = defineEmits(['toggle'])

const headerClasses = computed(() => ({
  'card-header': true,
  'bg-light': !props.envelope.active,
  'bg-primary': props.envelope.active,
}))

const headerId = computed(() => getEnvelopeContainerId('header'))
const bodyId = computed(() => getEnvelopeContainerId('body'))
const envelopeConsumption = computed(() => {
  const { amount = '0', outstanding = '0' } = props.envelope

  if (amount === '0') {
    return 100
  }

  return 100 * (Number(outstanding) / Number(amount))
})

const envelopeConsumed = computed(() => '0' !== (props.envelope.amount ?? '0') && envelopeConsumption.value >= 100)

function toggle() {
  emit('toggle')
}

function getEnvelopeContainerId(type: string): string {
  return [props.accordionId, type, props.envelope.id].join('_')
}
</script>

<template>
  <div class="card rounded-0 mb-0">
    <div :class="headerClasses" :id="headerId" data-test="funding-envelope-list-item-header">
      <div class="d-flex w-100 align-items-center">
        <div class="d-flex flex-column flex-grow-1 justify-content-center">
          <div class="d-flex">
            <div class="flex-grow-1">
              <strong>{{ envelope.name }}</strong>
            </div>
          </div>
          <div class="d-flex mt-2 mt-xl-0 flex-column flex-xl-row">
            <div class="badge badge-secondary">
              {{ $d(new Date(envelope.startAt), 'date') }} - {{ $d(new Date(envelope.endAt), 'date') }}
            </div>
            <div
              v-if="envelope.amount"
              :class="{
                badge: true,
                'ml-0': true,
                'ml-xl-2': true,
                'mt-2': true,
                'mt-xl-0': true,
                'badge-secondary': !envelopeConsumed,
                'badge-danger': envelopeConsumed,
              }"
              data-test="funding-envelope-list-item-budget"
            >
              {{ $n(Number(envelope.outstanding), 'currency') }} / {{ $n(Number(envelope.amount), 'currency') }} ({{
                $n(envelopeConsumption / 100, 'percent')
              }})
            </div>
          </div>
        </div>
        <div class="ml-3">
          <button
            class="btn btn-sm btn-block"
            type="button"
            data-toggle="collapse"
            aria-expanded="true"
            :aria-controls="bodyId"
            @click="toggle"
            data-test="funding-envelope-list-item-toggle-btn"
          >
            <i :class="expanded ? 'fa fa-angle-up' : 'fa fa-angle-down'"></i>
          </button>
        </div>
      </div>
    </div>
    <div
      :id="bodyId"
      :class="expanded ? ['collapse', 'show'] : ['collapse']"
      :data-parent="accordionId"
      :aria-labelledby="headerId"
      data-test="funding-envelope-list-item-body"
    >
      <div class="card-body">
        <div class="list-group mt-1">
          <div
            class="list-group-item"
            v-for="allocation in envelope.allocations"
            :key="allocation.id"
            data-test="funding-envelope-list-item-allocation"
          >
            <div class="row align-items-start">
              <div class="col-12 col-xl-6">
                <small>{{ allocation.unit.name }}</small>
              </div>
              <div class="col-12 col-xl-6">
                <FundingEnvelopeListItemConsumption
                  v-if="allocation.outstanding !== undefined"
                  :value="Number(allocation.outstanding)"
                  :total="Number(allocation.amount)"
                  :remaining="Number(allocation.remainingAmount)"
                  :inherited="allocation.type === FundingAllocationType.INHERITED"
                  data-test="funding-envelope-list-item-allocation-consumption"
                />
                <FundingEnvelopeListItemConsumption
                  :value="Number(allocation.clientOutstanding)"
                  :total="Number(allocation.amount)"
                  :remaining="Number(allocation.remainingAmount)"
                  :inherited="allocation.type === FundingAllocationType.INHERITED"
                  client
                  data-test="funding-envelope-list-item-client-consumption"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card-body {
  padding: 0;
}
</style>
